import React, { useState } from "react";
import {
  IActivityFormProps,
  TapWhatYouHearOption,
  TapWhatYouHearQuestion,
} from "src/types";
import showToast from "@utils/toast";
import { useActivityCoreData } from "src/hooks/useActivityCoreData";
import { newActivity, updateActivity } from "src/service/activities.services";
import ActivityCore from "./activities.core";
import { Edit, Trash2, Save, X } from "lucide-react";
import { getAssetUrl, getNextSerialId } from "@utils/id.generator";
import { useNavigate } from "react-router-dom";

const TapWhatYouHearActivityForm = ({
  lessonMeta,
  initialActivity,
  activityId,
}: IActivityFormProps<TapWhatYouHearQuestion>) => {
  const isEditing = !!initialActivity;

  const navigate = useNavigate();

  const [isLoading, setLoading] = useState<boolean>(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);

  const [audioUrl, setAudioUrl] = useState(
    initialActivity?.data.question.audioUrl || ""
  );
  const [options, setOptions] = useState<TapWhatYouHearOption[]>(
    initialActivity?.data.options || []
  );
  const [answer, setAnswer] = useState<string[]>(
    (initialActivity?.data.answer as any) || []
  );
  const [newOption, setNewOption] = useState({ text: "", audioUrl: "" });
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const reorderList = <T,>(list: T[], from: number, to: number): T[] => {
    const copy = [...list];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
  };

  const handleDragEnd = () => {
    if (
      draggedIdx === null ||
      dragOverIdx === null ||
      draggedIdx === dragOverIdx
    ) {
      setDraggedIdx(null);
      setDragOverIdx(null);
      return;
    }

    const newOptions = reorderList(options, draggedIdx, dragOverIdx);

    console.log({ draggedIdx, dragOverIdx, options, newOptions });
    setOptions(newOptions);
    setAnswer(newOptions.filter((o) => answer.includes(o.id)).map((o) => o.id));
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const addOption = () => {
    if (!newOption.text.trim() || !newOption.audioUrl.trim()) {
      return showToast(
        "Both audio code and text is required for each option",
        false
      );
    }

    const optionId = getNextSerialId(options);
    const opt: TapWhatYouHearOption = {
      id: optionId,
      text: newOption.text.trim(),
      audioUrl: newOption.audioUrl.trim(),
    };
    setOptions((prev) => [...prev, opt]);
    setNewOption({ text: "", audioUrl: "" });
  };

  const toggleAnswer = (id: string) => {
    setAnswer((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const removeOption = (id: string) => {
    setOptions((prev) => prev.filter((opt) => opt.id !== id));
    setAnswer((prev) => prev.filter((ans) => ans !== id));
  };

  const coreState = useActivityCoreData({
    instruction: initialActivity?.instruction,
    localizedInstruction: initialActivity?.localizedInstruction,
    difficulty: initialActivity?.difficulty,
    tags: initialActivity?.tags,
    isActive: initialActivity?.isActive || false,
    activityNumber: initialActivity?.activityNumber,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();
    const isValidId = isEditing ? initialActivity?.id : activityId;
    if (!isValidId || isValidId === null || isValidId === undefined) {
      return showToast("Activity Id not provided", false);
    }

    const payload: TapWhatYouHearQuestion = {
      id: isValidId,
      activityKind: "TapInWhatYouHear",
      instruction: coreState.coreData.instruction,
      localizedInstruction: coreState.coreData.localizedInstruction,
      activityNumber: coreState.coreData.activityNumber,
      lessonId: initialActivity?.lessonId ?? (lessonMeta?.lesson as string),
      difficulty: coreState.coreData.difficulty,
      tags: coreState.coreData.tags,
      isActive: coreState.coreData.isActive,
      data: {
        question: { audioUrl },
        options,
        answer: answer || [],
      },
    };

    const request = isEditing
      ? updateActivity(payload, isValidId)
      : newActivity(payload);
    const response = await request;
    if (response?.status !== "success") {
      showToast("There was error saving activity", false);
      setLoading(false);
    } else {
      setLoading(false);
      showToast(
        `${isEditing ? "Activity Updated" : "Activity Created"} successfully`,
        true
      );
      return navigate("/dashboard/levels/lessons/activities");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 max-w-4xl mx-auto bg-white p-6 rounded-lg shadow"
    >
      <h2 className="text-base font-semibold text-gray-800">
        {isEditing ? "Edit" : "Create"} Tap what you hear Activity
      </h2>

      <ActivityCore data={coreState} />

      <div className="flex flex-row gap-2">
        <input
          type="text"
          placeholder="Audio Code"
          value={audioUrl}
          onChange={(e) => setAudioUrl(e.target.value)}
          className="input-style"
          required
          maxLength={6}
        />
      </div>

      {audioUrl && (
        <audio controls className="mt-2 w-full">
          <source src={getAssetUrl("audio", audioUrl)} type="audio/mpeg" />
        </audio>
      )}

      <div className="space-y-4">
        <h3 className="text-base font-semibold text-gray-800">Add Option</h3>
        <div className="grid sm:grid-cols-2 gap-4 items-end">
          <input
            type="text"
            placeholder="Option text"
            value={newOption.text}
            onChange={(e) =>
              setNewOption({ ...newOption, text: e.target.value })
            }
            className="input-style"
          />
          <input
            type="text"
            placeholder="Audio Code"
            value={newOption.audioUrl}
            maxLength={6}
            onChange={(e) =>
              setNewOption({ ...newOption, audioUrl: e.target.value })
            }
            className="input-style"
          />
          <div className="sm:col-span-2">
            <button
              type="button"
              onClick={addOption}
              className="text-sm text-brand hover:underline"
            >
              + Add Option
            </button>
          </div>
        </div>
      </div>

      {options.length > 0 && (
        <div className="space-y-2 pt-1">
          <h3 className="text-md font-semibold text-gray-800">Options</h3>
          <div
            className="flex overflow-x-auto gap-4 pb-2"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDragEnd}
          >
            {options.map((opt, idx) => {
              const isEditing = editIdx === idx;
              return (
                <div
                  key={opt.id}
                  draggable={!isEditing}
                  onDragStart={() => setDraggedIdx(idx)}
                  onDragEnter={() => setDragOverIdx(idx)}
                  className={`min-w-[180px] bg-white rounded-lg shadow-md px-4 py-3 border transition hover:shadow-lg
              ${
                answer.includes(opt.id)
                  ? "border-emerald-500"
                  : "border-gray-200"
              }
              ${draggedIdx === idx ? "opacity-50" : ""}
            `}
                >
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={opt.text}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((o, i) =>
                              i === idx ? { ...o, text: e.target.value } : o
                            )
                          )
                        }
                        className="w-full px-2 py-1 border rounded-md focus:ring focus:ring-brand mb-2"
                        placeholder="Option Text"
                      />
                      <input
                        type="text"
                        value={opt.audioUrl || ""}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((o, i) =>
                              i === idx ? { ...o, audioUrl: e.target.value } : o
                            )
                          )
                        }
                        className="w-full px-2 py-1 border rounded-md focus:ring focus:ring-brand mb-2"
                        placeholder="Audio Code"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setEditIdx(null)}
                          className="text-green-600 hover:text-green-800"
                          title="Save"
                        >
                          <Save size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditIdx(null)}
                          className="text-gray-500 hover:text-gray-700"
                          title="Cancel"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-800">
                        {opt.text}
                      </p>
                      {opt.audioUrl && (
                        <audio controls className="w-full mt-2 h-7 rounded">
                          <source
                            src={getAssetUrl("audio", opt.audioUrl)}
                            type="audio/mpeg"
                          />
                        </audio>
                      )}
                      <div className="mt-3 flex justify-between items-center text-sm">
                        <button
                          type="button"
                          onClick={() => toggleAnswer(opt.id)}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition ${
                            answer.includes(opt.id)
                              ? "bg-brand text-white hover:bg-brand-dark"
                              : "text-brand border border-emerald-300 hover:bg-emerald-50"
                          }`}
                        >
                          {answer.includes(opt.id) ? "✓ Selected" : "Select"}
                        </button>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setEditIdx(idx)}
                            className="text-blue-500 hover:text-blue-700"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeOption(opt.id)}
                            className="text-red-500 hover:text-red-700"
                            title="Remove"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pt-4">
        <button type="submit" className="button-primary" disabled={isLoading}>
          {isEditing ? "Update Activity" : "Create Activity"}
        </button>
      </div>
    </form>
  );
};
export default TapWhatYouHearActivityForm;
